import { usePkce } from "../Api/usePkce";
import { useAuthStore } from "./useAuthStore";

const COOLDOWN_MS = 30_000;
// The redirect reloads the page, so the cooldown has to outlive it; module state would start at zero again.
const LAST_ATTEMPT_KEY = "devSilentReauthorize.lastAttemptAt";

function readLastAttemptAt(): number {
  try {
    return Number(sessionStorage.getItem(LAST_ATTEMPT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeLastAttemptAt(value: number): void {
  try {
    sessionStorage.setItem(LAST_ATTEMPT_KEY, String(value));
  } catch {
    // Without storage the cooldown only lasts for this page load.
  }
}

/**
 * Dev-only recovery when a refresh token was rotated/lost (typical HMR mid-flight).
 * Restarts PKCE authorize; if the auth-server session cookie is still valid, the user
 * comes back with a fresh pair without a credential prompt. The rejected tokens are dropped first: left in
 * storage, the app would refresh the same token again on the callback before exchanging the new code.
 * Returns true when a redirect was started so the caller should not logout.
 */
export function attemptDevSilentReauthorize(): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }

  const now = Date.now();
  if (now - readLastAttemptAt() < COOLDOWN_MS) {
    return false;
  }
  writeLastAttemptAt(now);

  try {
    const { generateAuthorizationUrl } = usePkce();
    const url = generateAuthorizationUrl();
    useAuthStore().reset(true);
    window.location.replace(url);
    return true;
  } catch (e) {
    console.error("[devSilentReauthorize] Failed to start PKCE authorize", e);
    return false;
  }
}

/** Test-only: reset cooldown between cases. */
export function __resetDevSilentReauthorizeForTests() {
  sessionStorage.removeItem(LAST_ATTEMPT_KEY);
}
